import {
  FieldButton,
  FlexBox,
  FormField,
  FormGrid,
  Page,
  Text,
} from '@dixa/lib-shared-habla';
import { ApiErrorStatusCode, isQueryError, QueryError } from 'lib-shared-auth';
import { Satisfaction } from 'lib-shared-legacy-types';
import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { ratingApi } from 'apis/rating';
import { AppLink } from 'components/appLink';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { validateName } from 'utils/formValidator';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { SurveyLanguageDropdown } from './components/languages/components/dropdown/surveyLanguagesDropdown';
import { SurveyLanguagesTable } from './components/languages/components/table/surveyLanguagesTable';
import styles from './survey.module.scss';

type FieldValues = { name: string; sender_name: string };

export const SatisfactionSurveyView = (): JSX.Element => {
  const { settingsId } = useAppPathParams(
    '/settings/satisfaction/phone/survey/:settingsId',
  );

  const notify = useNotifications();
  const navigate = useAppNavigate();

  const { data: surveyRatingSettings } = ratingApi.useGetRatingSettingsQuery({
    ratingSettingsId: settingsId,
  });

  const {
    data: languageRatingSettings,
    isSuccess: listLanguageSettingIsSuccess,
  } = ratingApi.useReadRatingSettingsQuery({
    level: 'language',
    parentId: settingsId,
    surveyType: surveyRatingSettings?.data.survey_type,
  });

  const [updateRatingSettings] = ratingApi.useLazyUpdateRatingSettingsQuery();

  const handleGoToLanguage = useCallback(
    (languageCode: string) => {
      if (surveyRatingSettings?.data.survey_type) {
        const surveyTypeSegment =
          surveyRatingSettings?.data.survey_type === 'text_to_speech'
            ? 'ivr'
            : 'sms';
        navigate({
          path: `/settings/satisfaction/phone/survey/${surveyTypeSegment}/:settingsId/:languageCode`,
          params: {
            path: {
              settingsId: settingsId,
              languageCode: languageCode,
            },
          },
        });
      }
    },
    [navigate, settingsId, surveyRatingSettings],
  );

  const form = useForm<FieldValues>({
    mode: 'all',
  });

  // Finishes initializing after the data has been fetched.
  useEffect(() => {
    if (listLanguageSettingIsSuccess) {
      form.setValue('name', surveyRatingSettings?.data.name ?? '');
    }
    if (
      listLanguageSettingIsSuccess &&
      surveyRatingSettings?.data.survey_type === 'link'
    ) {
      try {
        const linkRatingCustomization: Satisfaction.LinkRatingCustomization =
          surveyRatingSettings?.data
            .customization as Satisfaction.LinkRatingCustomization;
        form.setValue(
          'sender_name',
          linkRatingCustomization?.sender_name ?? '',
        );
      } catch {
        {
        }
      }
    }
  }, [form, listLanguageSettingIsSuccess, surveyRatingSettings?.data.name]);

  const handleValidSubmit: SubmitHandler<FieldValues> = async (values) => {
    try {
      const updateReq: Satisfaction.UpdateTextToSpeechCsatSettingsParams = {
        id: settingsId,
        name: values.name,
      };
      await updateRatingSettings(updateReq).unwrap();

      notify(
        {
          title: 'Success',
          text: `The survey has been updated.`,
          icon: 'done',
          time: 5000,
        },
        NotificationType.Success,
      );

      form.reset({}, { keepValues: true });
    } catch (error: unknown | QueryError) {
      const title =
        error &&
        isQueryError(error) &&
        error.status === ApiErrorStatusCode.Conflict
          ? 'Survey name already exists.'
          : 'Error while saving the survey.';

      notify(
        {
          title,
        },
        NotificationType.Error,
      );
    }
  };

  return (
    <>
      <Page.Section
        titleMeta={
          <AppLink path="/settings/satisfaction/phone">
            ← Back to survey list
          </AppLink>
        }
      >
        <FlexBox flexDirection="column" gap="400">
          {surveyRatingSettings?.data?.survey_type && (
            <FlexBox gap={'200'}>
              <Text.Paragraph variant="1" subdued>
                Survey type
              </Text.Paragraph>

              <Text.Paragraph variant="1">
                {Satisfaction.prettySurveyType(
                  surveyRatingSettings?.data.survey_type,
                )}
              </Text.Paragraph>
            </FlexBox>
          )}

          <form
            noValidate
            tabIndex={-1}
            onSubmit={form.handleSubmit(handleValidSubmit)}
          >
            <FormGrid>
              <FormField span={2}>
                <HookFormText
                  name="name"
                  label="CSAT survey name"
                  control={form.control}
                  rules={{
                    required: 'Name is required',
                    validate: validateName,
                  }}
                  componentProps={{
                    placeholder: 'e.g. Lalalala',
                  }}
                />
              </FormField>

              <FormField span={1}>
                <FieldButton
                  variant="primary"
                  className={styles.inputSurveySaveButton}
                  disabled={
                    !form.formState.isDirty ||
                    !form.formState.isValid ||
                    form.formState.isSubmitting
                  }
                  type="submit"
                >
                  Save
                </FieldButton>
              </FormField>
            </FormGrid>
          </form>
        </FlexBox>
      </Page.Section>

      {surveyRatingSettings?.data && (
        <Page.Section
          title="Language options"
          controls={
            <SurveyLanguageDropdown
              surveyType={surveyRatingSettings?.data.survey_type}
              handleGoToLanguage={handleGoToLanguage}
              languageSettings={
                languageRatingSettings
                  ? languageRatingSettings.data.rating_settings
                  : []
              }
            />
          }
        >
          <SurveyLanguagesTable
            handleGoToLanguage={handleGoToLanguage}
            surveyCsatSettings={surveyRatingSettings.data}
          />
        </Page.Section>
      )}
    </>
  );
};
